<template>
  <BaseModal
    v-bind="bindings"
    :is-open="isOpen"
    @close="$emit('close')"
    @back="$emit('back')"
    @cancel="onCancel"
  >
    <template #default>
      <div class="base-modal__content">
        <slot
          name="default"
          :on-cancel="onCancel"
          :on-submit="onSubmit"
          :is-open="isOpen"
        />
      </div>
      <div
        v-if="!noControls"
        class="base-modal__controls"
        :class="{ 'horizontal': horizontalButtons }"
      >
        <slot
          name="controls"
          :on-cancel="onCancel"
          :on-submit="onSubmit"
          :close="close"
          :is-loading="isLoading"
        >
          <slot name="cancel" :on-cancel="onCancel" />
          <slot name="submit" :on-submit="onSubmit" />
        </slot>
      </div>
    </template>
    <template #gradient>
      <slot name="gradient" />
    </template>
  </BaseModal>
</template>

<script>
import { disablePageScroll, enablePageScroll, addScrollableSelector, removeScrollableSelector } from 'scroll-lock'
import BaseModal from '@/components/Base/Modal'
import { allModalsProps, baseAndLocalModalProps, bindings_allModalsProps, bindings_baseAndLocalModalProps, bindings_localModalAndItsDescendantsProps, localModalAndItsDescendantsProps } from '@/components/Base/Modal/sharedProps'
import { SCROLLABLE_SELECTOR } from '~/components/Base/Modal/utils.js'

/**
 * === slots ===
 * default: content
 * controls: replace default submit and cancel buttons and allow adds more
 * cancel: negative action
 * submit: positive action
 * */

/**
 * == emits ==
 * input = modal state, is modal open
 * close = same as 'input, but triggers only when modal closing (input = false)
 * submit = submit action of the modal, positive event. also trigger 'close'
 * cancel = X btn, btn cancel, click outside
 * */

// .$children[0].open()

const QUERY_KEY = 'modal'
let resolve
let asyncResolve
let isOpenedByAsyncOpen

export default {
  name: 'BaseModalLocal',
  components: { BaseModal },
  mixins: [
    bindings_allModalsProps,
    bindings_baseAndLocalModalProps,
    bindings_localModalAndItsDescendantsProps,
  ],
  props: {
    ...allModalsProps,
    ...baseAndLocalModalProps,
    ...localModalAndItsDescendantsProps,
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
    }
  },
  computed: {
    bindings() {
      return { ...this.allModalsProps, ...this.baseAndLocalModalProps }
    },
    c_queryKey() {
      return this.queryKey ? this.queryKey : QUERY_KEY
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.isOpen = this.value
      },
    },
    isOpen() {
      this.$nextTick(() => {
        if (this.isOpen) {
          this.disableScroll()
        }
        else {
          this.enableScroll()
          this.removeQuery()
        }
      })
    },
    '$route.path': function(newVal, oldVal) {
      if (newVal === oldVal || !this.isOpen || this.persistOnRoutePathChange) return
      this.close()
    },
    '$route.query': 'initByQuery',
  },
  created() {
    this.initByQuery()
    if (process.client && this.value) this.disableScroll()
  },
  beforeDestroy() {
    this.enableScroll()
  },
  methods: {
    disableScroll() {
      addScrollableSelector(SCROLLABLE_SELECTOR)
      disablePageScroll()
    },
    enableScroll() {
      this.$nextTick(() => {
        removeScrollableSelector(SCROLLABLE_SELECTOR)
        enablePageScroll()
      })
    },
    initByQuery() {
      if (
        process.server
        || !this.query
        || !this.$route.query[this.c_queryKey]
        || this.$route.query[this.c_queryKey] !== this.query
      ) return
      this.isOpen = true
    },
    addQuery() {
      if (!this.query) return
      const oldQuery = { ...this.$route.query }
      oldQuery[this.c_queryKey] = this.query
      this.$router.replace({ query: oldQuery })
    },
    removeQuery() {
      if (!this.query || !this.$route.query[this.c_queryKey]) return
      const oldQuery = { ...this.$route.query }
      delete oldQuery[this.c_queryKey]
      this.$router.replace({ query: oldQuery })
    },
    async open() {
      // deprecated, use $open
      this.isOpen = true
      this.addQuery()
      return new Promise((res) => {
        resolve = res
      })
    },
    async $asyncOpen() {
      isOpenedByAsyncOpen = true
      return await this.$open()
    },
    close() {
      // deprecated, use $close
      this.isOpen = false
      this.$emit('close')
      this.$emit('input', false)
      isOpenedByAsyncOpen = false
    },
    onSubmit() {
      if (isOpenedByAsyncOpen) return this.asyncSubmit()
      this.close()
      if (resolve) resolve(true)
      this.$emit('submit')
    },
    asyncSubmit() {
      this.isLoading = true
      new Promise((resolveFn) => {
        asyncResolve = resolveFn
      })
        .then((res) => {
          this.isLoading = false
          this.close()
          this.$emit('submit')
        })
      if (resolve) resolve(asyncResolve)
    },
    onCancel() {
      this.close()
      if (resolve) resolve(false)
      this.$emit('cancel')
    },
    /** Exposed methods */
    $close() {
      this.close()
    },
    async $open() {
      return await this.open()
    },
    $submit() {
      this.onSubmit()
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
